import { zahl } from '../cockpit/format';
import { GELD } from '../billing/geldWoerter';
import { EBENEN } from '../../config/ebenen';

// Profil-Nachbau F-06/F-20, PF-313-1 (BAU-HILFE.md 9.0 A-13/A-14, 9.1, 9.1.1; TEIL-3 1.0 „Anzeigewörter“, SP3-08): alle Wörter
// des Support-Arbeitsplatzes in der Plattform-Verwaltung und die reinen Funktionen dazu. Rein, ohne JSX. Die .tsx-Dateien des
// Arbeitsplatzes schreiben keine sichtbaren Texte selbst. Status mit den Wörtern des Betreibers (A-13), nicht der Kundenseite.

export const SUPPORT_UNTERBEREICHE = ['tickets', 'stoerungen', 'wuensche', 'hilfe', 'neuigkeiten'] as const;
export type SupportUnterbereich = typeof SUPPORT_UNTERBEREICHE[number];

/** Je Unterbereich genau ein h1 (BAU-HILFE 9.1, wörtlich). */
export const SUPPORT_TITEL: Record<SupportUnterbereich, string> = {
  tickets: 'Tickets',
  stoerungen: 'Störungen',
  wuensche: 'Wünsche',
  hilfe: 'Hilfe-Dokumente und Chatbot',
  neuigkeiten: 'Neuigkeiten',
};

// Profil-Nachbau F-23 (PF-NB-hilfe-2; Nutzerblick Inhaber H-04): Support-Anfragen stehen nur unter „Support“ › „Tickets“. „System“
// zeigt unter ?system=tickets nur noch diesen Hinweis (system/SystemAdministration.tsx SupportVerweis).
export const SYSTEM_SUPPORT_TEXTE = {
  titel: 'Supporttickets',
  satz: 'Alle Support-Anfragen bearbeiten Sie unter „Support“ › „Tickets“.',
  link: 'Zu den Tickets',
} as const;

/** Statusfilter der Liste, wie der Server sie kennt (GET /support/tickets?status=…); Vorgabe „Wartet auf uns“. */
export const LISTEN_FILTER = ['wartet_auf_uns', 'wartet_auf_kunde', 'offen', 'geloest', 'geschlossen', 'alle'] as const;
export type ListenFilter = typeof LISTEN_FILTER[number];
export const ZEITRAEUME = ['30', '90', '365'] as const;
export type Zeitraum = typeof ZEITRAEUME[number];

export const SUPPORT_TEXTE = {
  eyebrow: 'Plattformverwaltung',
  // BAU-HILFE 9.1: das Wort des Rechts aus capabilities.ts.
  ohneSchreibrecht: 'Zum Bearbeiten fehlt Ihnen das Recht „Supportanfragen bearbeiten“.',
  rechteLaden: 'Ihre Rechte werden geladen …',
  kennzahlen: {
    titel: 'Kennzahlen',
    zeitraum: 'Zeitraum',
    zeitraeume: { '30': '30 Tage', '90': '90 Tage', '365': '12 Monate' } as Record<Zeitraum, string>,
    offen: 'Offen',
    wartetAufUns: 'Wartet auf uns',
    wartetAufKunde: 'Wartet auf Kunde',
    erstreaktion: 'Erstreaktion (Median)',
    geloest: 'Gelöst im Zeitraum',
    laden: 'Kennzahlen werden geladen …',
  },
  jeFirma: {
    titel: 'Tickets je Firma',
    firma: 'Firma',
    offen: 'Offen',
    imZeitraum: 'Im Zeitraum',
    leer: 'Keine Tickets im Zeitraum.',
    ohneName: 'Firma ohne Namen',
  },
  liste: {
    titel: 'Alle Tickets',
    status: 'Status',
    mirZugewiesen: 'Mir zugewiesen',
    nichtZugewiesen: 'Nicht zugewiesen',
    zuweisung: 'Zuweisung',
    kategorie: 'Kategorie',
    alleKategorien: 'Alle Kategorien',
    prioritaet: 'Priorität',
    allePrioritaeten: 'Alle Prioritäten',
    firma: 'Firma',
    firmaSuchen: 'Firma suchen',
    alleFirmen: 'Alle Firmen',
    firmaTreffer: 'Treffer der Firmensuche',
    keineFirma: 'Keine Firma gefunden.',
    suche: 'Referenz, Betreff, Person oder Firma',
    suchen: 'Suchen',
    leer: 'Keine Tickets in dieser Auswahl.',
    laden: 'Tickets werden geladen …',
    ohneFirma: 'ohne Firma',
    ohnePerson: '—',
    niemand: 'Niemand',
    spalten: {
      referenz: 'Referenz', betreff: 'Betreff', firma: 'Firma', person: 'Person', kategorie: 'Kategorie', prioritaet: 'Priorität',
      status: 'Status', wartetSeit: 'wartet seit', zugewiesen: 'zugewiesen',
    },
  },
  filter: {
    wartet_auf_uns: 'Wartet auf uns', wartet_auf_kunde: 'Wartet auf Kunde', offen: 'Offen', geloest: 'Gelöst', geschlossen: 'Geschlossen',
    alle: 'Alle',
  } as Record<ListenFilter, string>,
} as const;

// A-13: die Wörter des Betreibers; die Farbe der Marke folgt TEIL-3 1.0 (support.css, sup-status--…).
const STATUS: Record<string, string> = { NEU: 'Neu', IN_BEARBEITUNG: 'In Arbeit', WARTET_AUF_KUNDE: 'Wartet auf Antwort', GELOEST: 'Gelöst',
  GESCHLOSSEN: 'Geschlossen' };
// F-23 (PF-NB-hilfe-3): ANTWORT_FALSCH an zweiter Stelle wie im Server (backend/src/support/regeln.ts) und auf der Kundenseite.
export const KATEGORIEN = ['FEHLER', 'ANTWORT_FALSCH', 'FRAGE', 'KONTO', 'ABRECHNUNG', 'WUNSCH', 'SONSTIGES'] as const;
const KATEGORIE: Record<string, string> = { FEHLER: 'Etwas funktioniert nicht', ANTWORT_FALSCH: 'Falsche oder zweifelhafte Antwort', FRAGE: 'Frage zur Bedienung', KONTO: 'Konto und Zugang',
  ABRECHNUNG: 'Rechnung und Tarif', WUNSCH: 'Idee oder Wunsch', SONSTIGES: 'Sonstiges' };
export const PRIORITAETEN = ['NIEDRIG', 'NORMAL', 'HOCH', 'DRINGEND'] as const;
const PRIORITAET: Record<string, string> = { NIEDRIG: 'Niedrig', NORMAL: 'Normal', HOCH: 'Hoch', DRINGEND: 'Dringend' };
const OHNE_WORT = '—';

/** Das Wort des Betreibers zum Status; ein unbekannter Wert „—“. */
export const statusWort = (status: string) => STATUS[status] ?? OHNE_WORT;
/** Klasse der Statusmarke (support.css): sup-status--neu, --in-bearbeitung, --wartet-auf-kunde, --geloest, --geschlossen. */
export const statusKlasse = (status: string) => `sup-status sup-status--${STATUS[status] ? status.toLowerCase().replace(/_/g, '-') : 'unbekannt'}`;
export const kategorieWort = (kategorie: string) => KATEGORIE[kategorie] ?? OHNE_WORT;
export const prioritaetWort = (prioritaet: string) => PRIORITAET[prioritaet] ?? OHNE_WORT;

/** „{h} Std. {m} Min.“, unter einer Stunde „{m} Min.“, ohne Wert „—“. */
export function erstreaktionText(minuten: number | null) {
  if (minuten === null || !Number.isFinite(minuten)) return OHNE_WORT;
  const ganz = Math.max(0, Math.round(minuten)), stunden = Math.floor(ganz / 60), rest = ganz % 60;
  return stunden ? `${stunden} Std. ${rest} Min.` : `${rest} Min.`;
}

const ZEIT = new Intl.DateTimeFormat('de-AT', { timeZone: 'Europe/Vienna', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
/** ISO-Zeitpunkt → „14.10.2026, 09:30“ in Wien. */
export const zeitpunkt = (iso: string) => ZEIT.format(new Date(iso));

// PF-313-2a (BAU-HILFE.md 9.1.1 „Detail“, „Verlauf“, „Antworten“, „Seitenleiste des Details“; A-13, A-19; TEIL-3 SP3-08): das
// Ticket als Seite. Fehlersätze zu Dateien wie backend/src/support/anhaenge.ts. Zahlen mit Tausenderpunkt (cockpit/format.ts zahl).
export const TICKET_TEXTE = {
  zurueck: 'Zurück zu den Tickets',
  titel: (referenz: string, betreff: string) => `${referenz} · ${betreff}`,
  nichtGefunden: 'Diese Anfrage gibt es nicht.',
  laden: 'Das Ticket wird geladen …',
  kopf: {
    titel: 'Angaben zur Anfrage',
    firma: 'Firma', person: 'Person', rolle: 'Rolle', tarif: 'Tarif', herkunft: 'Herkunft', referenz: 'Referenz', kontingent: 'Kontingent',
    einzelkonto: 'Einzelkonto', ohneFirma: 'ohne Firma', ohneWert: '—',
    seite: (pfad: string) => `Seite: ${pfad}`, geraet: (geraet: string) => `Gerät: ${geraet}`, version: (version: string) => `Version: ${version}`,
    kontingentPerson: (wert: string) => `Person: ${wert}`, kontingentFirma: (wert: string) => `Firma: ${wert}`,
    prozent: (wert: number) => `${zahl(wert)} %`, euro: (betrag: string) => ` · ${betrag}`,
  },
  rollen: { BESITZER: 'Besitzer', ADMINISTRATOR: 'Administrator', MITGLIED: 'Mitglied', EINZELKONTO: 'Einzelkonto' } as Record<string, string>,
  quellen: { FORMULAR: 'Formular', ASSISTENT: 'Chat', BETREIBER: 'Betreiber', IMPORT: 'Früheres Supportsystem', ANMELDUNG: 'Anmeldeseite' } as Record<string, string>,
  gespraech: { titel: 'Gespräch mit dem Hilfe-Assistenten', sie: 'Sie', assistent: 'Hilfe-Assistent' },
  verknuepft: {
    stoerung: (titel: string) => `Störung: ${titel}`,
    wunsch: (referenz: string, titel: string) => `Wunsch: ${referenz} ${titel}`,
    in: 'Zusammengeführt in', aus: 'Zusammengeführt aus',
  },
  verlauf: {
    titel: 'Verlauf', intern: 'Intern', system: 'System', support: 'Support', unbekannt: '—',
    fuerFirma: (name: string) => `${name} für die Firma`,
    anhang: (name: string, groesse: string) => `${name} (${groesse})`,
  },
  antwort: {
    titel: 'Antworten', art: 'Art der Nachricht', anPerson: 'Antwort an die Person', notiz: 'Interne Notiz',
    vorlage: 'Vorlage einfügen', vorlageWaehlen: 'Vorlage wählen', feldAntwort: 'Antwort', feldNotiz: 'Notiz',
    zeichen: (anzahl: number) => `${zahl(anzahl)} von 10.000 Zeichen`,
    dateien: 'Dateien', dateiHinweis: 'Bis zu 3 Dateien, je höchstens 10 MB und zusammen höchstens 20 MB.',
    dateiEntfernen: (name: string) => `${name} entfernen`,
    erwaehnen: 'Betreiberkonto erwähnen',
    antworten: 'Antworten', antwortenWarten: 'Antworten und auf Antwort warten', antwortenGeloest: 'Antworten und als gelöst markieren',
    notizSpeichern: 'Notiz speichern', gesendet: 'Antwort gesendet.', gespeichert: 'Notiz gespeichert.',
    euroTitel: 'Betrag in Euro',
    euroFrage: 'Die Antwort nennt einen Betrag in Euro. Firmen sehen Kosten sonst nur in Prozent. Trotzdem senden?',
    abbrechen: 'Abbrechen', trotzdem: 'Trotzdem senden',
  },
  leiste: {
    titel: 'Bearbeitung', status: 'Status', prioritaet: 'Priorität', kategorie: 'Kategorie', zugewiesen: 'Zugewiesen an', niemand: 'Niemand',
    unbekannt: '—', verknuepfungen: 'Verknüpfungen', stoerung: 'Störung', keine: 'Keine', wunsch: 'Wunsch',
    wunschSuche: 'Referenz oder Titel', suchen: 'Suchen', keiner: 'Keiner', keinTreffer: 'Kein Wunsch gefunden.',
    wunschTreffer: 'Treffer der Wunschsuche', ohneWunsch: 'Kein Wunsch verknüpft.',
    stoerungZeile: (titel: string, status: string) => `${titel} (${status})`,
    wunschZeile: (referenz: string, titel: string) => `${referenz} ${titel}`,
  },
  stoerungStatus: { UNTERSUCHUNG: 'Wird untersucht', ERKANNT: 'Ursache gefunden', BEOBACHTUNG: 'Behoben, wird beobachtet', BEHOBEN: 'Behoben' } as Record<string, string>,
  dateiFehler: { anzahl: 'Höchstens 3 Dateien je Nachricht.', groesse: 'Die Datei ist größer als 10 MB.', gesamt: 'Die Dateien sind zusammen größer als 20 MB.' },
} as const;

// PF-313-2b (BAU-HILFE.md 9.1.1 Knöpfe der Seitenleiste und „Anfrage erfassen“, A-15, 9.3.3; TEIL-3 SP3-05 Wörter der Störungen,
// SP3-08 „Sicht starten“, SP3-03 „Wer darf diese Anfrage sehen?“). Das Wort des Bereichs „Verwaltung“ steht nur in
// config/ebenen.ts (VE-13) und kommt von dort.
// F-23 (PF-NB-hilfe-2, F-23-hilfe-1): beim Betreiber heißt eine Support-Anfrage „Ticket“ (Nutzerblick Inhaber H-04).
export const AKTIONEN_TEXTE = {
  oeffentlich: 'Titel und Meldung sind öffentlich. Bitte keine Namen von Personen oder Firmen.',
  abbrechen: 'Abbrechen',
  stoerung: {
    knopf: 'Störung daraus anlegen …', titel: 'Störung daraus anlegen',
    feldTitel: 'Titel', auswirkung: 'Auswirkung', bereiche: 'Bereiche', beginn: 'Beginn', ersteMeldung: 'Erste Meldung',
    sofort: 'Sofort veröffentlichen', anlegen: 'Störung anlegen', angelegt: 'Störung angelegt und mit dem Ticket verknüpft.',
  },
  auswirkungen: { GERING: 'Gering', TEILWEISE: 'Teilweise eingeschränkt', ERHEBLICH: 'Stark eingeschränkt', AUSFALL: 'Ausfall' } as Record<string, string>,
  bereiche: { RECHERCHE: 'Recherche', KOLLEKTIVVERTRAEGE: 'Kollektivverträge', ANMELDUNG: 'Anmeldung', ABRECHNUNG: 'Abrechnung',
    VERWALTUNG: EBENEN.verwaltung, MAIL: 'E-Mails' } as Record<string, string>,
  wunsch: {
    knopf: 'Wunsch daraus anlegen …', titel: 'Wunsch daraus anlegen',
    feldTitel: 'Titel', beschreibung: 'Beschreibung', sichtbarkeit: 'Sichtbarkeit', alleFirmen: 'Alle Kundenfirmen', dieseFirma: 'Nur diese Firma',
    folgt: 'Die Person folgt dem Wunsch und erfährt Änderungen des Status.', anlegen: 'Wunsch anlegen',
    angelegt: (referenz: string) => `Wunsch ${referenz} angelegt und mit dem Ticket verknüpft.`,
  },
  zusammen: {
    knopf: 'Mit anderem Ticket zusammenführen …', titel: 'Mit anderem Ticket zusammenführen', ziel: 'Ziel',
    laden: 'Offene Tickets der Person werden geladen …', keine: 'Diese Person hat kein weiteres offenes Ticket.',
    kandidat: (referenz: string, betreff: string, status: string) => `${referenz} · ${betreff} (${status})`,
    frage: (quelle: string, ziel: string) => `${quelle} in ${ziel} zusammenführen? ${quelle} wird geschlossen; Verlauf und Anhänge bleiben dort lesbar. Die Person sieht in beiden einen Hinweis.`,
    knopfZusammen: 'Zusammenführen',
  },
  sichtGrund: (referenz: string) => `Supportanfrage ${referenz}`,
  erfassen: {
    knopf: 'Ticket erfassen', titel: 'Ticket erfassen', person: 'Person', personPflicht: 'Person (Pflicht)', personSuche: 'Name oder E-Mail-Adresse',
    suchen: 'Suchen',
    personTreffer: 'Treffer der Personensuche', keinePerson: 'Keine Person gefunden.', andere: 'Andere Person wählen',
    personZeile: (name: string, email: string, firma: string) => `${name} · ${email} · ${firma}`, ohneFirma: 'ohne Firma', einzelkonto: 'Einzelkonto',
    kategorie: 'Kategorie', kategorieWaehlen: 'Bitte wählen', betreff: 'Betreff', text: 'Text',
    wer: 'Wer darf dieses Ticket sehen?', firma: 'Der Support und die Verwaltung der Firma', privat: 'Nur der Support und die Person',
    anlegen: 'Ticket anlegen',
    erfolg: (referenz: string) => `Ticket ${referenz} ist angelegt. Die Person erhält eine Eingangsbestätigung nach ihren Benachrichtigungen.`,
    oeffnen: 'Ticket öffnen',
  },
} as const;

// PF-324a (BAU-HILFE.md 9.1.4 mit 9.4 „Folgen“, A-16; TEIL-3 SP3-08 „Hilfe-Assistent“; F21-3): Hilfe-Dokumente und Chatbot.
export const PFLEGE_TEXTE = {
  eingebaut: {
    titel: 'Eingebaute Anleitungen',
    anzahl: (anzahl: number) => (anzahl === 1 ? '1 Anleitung' : `${zahl(anzahl)} Anleitungen`),
    satz: (produkt: string) => `Diese Anleitungen kommen mit ${produkt} und ändern sich mit neuen Versionen.`,
    aufklapper: 'Fragen anzeigen',
  },
  dokumente: {
    titel: 'Eigene Hilfe-Dokumente', laden: 'Hilfe-Dokumente werden geladen …', leer: 'Noch keine eigenen Hilfe-Dokumente.',
    spalten: { titel: 'Titel', zielgruppe: 'Zielgruppe', stand: 'Stand', geaendert: 'geändert' },
    anlegen: 'Dokument anlegen', dialog: 'Hilfe-Dokument', nichtGefunden: 'Dieses Hilfe-Dokument gibt es nicht.',
    feldTitel: 'Titel', feldText: 'Text', zaehler: (anzahl: number) => `${zahl(anzahl)} von 5.000 Zeichen`,
    stichwoerter: 'Stichwörter', stichwortHinweis: 'Stichwort eingeben und mit der Eingabetaste hinzufügen, höchstens 20.',
    stichwortEntfernen: (wort: string) => `${wort} entfernen`, stichwortListe: 'Gewählte Stichwörter',
    zielgruppe: 'Zielgruppe',
    hinweis: 'Veröffentlichte Dokumente sehen alle Kundinnen und Kunden unter „Hilfe und Feedback“. Der Hilfe-Assistent nutzt sie als Wissen.',
    entwurfSpeichern: 'Als Entwurf speichern', veroeffentlichen: 'Veröffentlichen', zurueckziehen: 'Zurückziehen',
    gespeichert: 'Hilfe-Dokument gespeichert.', veroeffentlicht: 'Hilfe-Dokument veröffentlicht.', zurueckgezogen: 'Hilfe-Dokument zurückgezogen.',
    titelFehler: 'Bitte 5 bis 150 Zeichen.', textFehler: 'Bitte 1 bis 5.000 Zeichen.',
  },
  zielgruppen: { ALLE: 'Alle', VERWALTUNG: 'Nur Verwaltende' } as Record<string, string>,
  stand: { entwurf: 'Entwurf', veroeffentlicht: 'Veröffentlicht' },
  assistent: {
    titel: 'Hilfe-Assistent', laden: 'Einstellung wird geladen …',
    schalter: 'Hilfe-Assistent für Kundinnen und Kunden', immer: 'Für Betreiberkonten ist der Hilfe-Assistent immer an.',
    ohneRecht: 'Zum Ändern fehlt Ihnen das Recht „Systemeinstellungen ändern“.',
    frageTitel: 'Hilfe-Assistent einschalten', frage: 'Einschalten? Jede Frage verursacht KI-Kosten beim Anbieter; Kunden belastet das nicht.',
    einschalten: 'Einschalten', abbrechen: 'Abbrechen',
    eingeschaltet: 'Der Hilfe-Assistent ist für Kundinnen und Kunden eingeschaltet.', ausgeschaltet: 'Der Hilfe-Assistent ist für Kundinnen und Kunden ausgeschaltet.',
    kennzahlen: 'Kennzahlen (ohne Gesprächsinhalte)', unterhaltungen: 'Unterhaltungen', mitTicket: 'Anteil mit Ticket', rechtsfragen: 'Anteil Rechtsfragen',
    prozent: (wert: number | null) => (wert === null ? '—' : `${zahl(wert)} %`),
    meistgenannt: 'Meistgenannte Anleitungen', anleitung: 'Anleitung', nennungen: 'Nennungen', keineNennungen: 'Im Zeitraum keine Anleitung genannt.',
    unbekannt: 'Anleitung nicht mehr vorhanden', kosten: 'KI-Kosten',
    satz: 'Unterhaltungen sind nicht lesbar, außer eine Person hängt sie an ein Ticket.',
  },
  vorlagen: {
    titel: 'Antwortvorlagen', laden: 'Antwortvorlagen werden geladen …', leer: 'Noch keine Antwortvorlagen.',
    spalten: { titel: 'Titel', geaendert: 'geändert' }, anlegen: 'Vorlage anlegen', dialog: 'Antwortvorlage',
    feldTitel: 'Titel', feldText: 'Text', platzhalter: 'Platzhalter: {Vorname}, {Referenz}, {Produktname}', speichern: 'Vorlage speichern',
    gespeichert: 'Antwortvorlage gespeichert.', titelFehler: 'Bitte 3 bis 80 Zeichen.', textFehler: 'Bitte 1 bis 5.000 Zeichen.',
  },
} as const;

// PF-324b (BAU-HILFE.md 9.1.5 mit 9.4 „Folgen“, 9.2 Punkt 2; F-21 Punkt 9; TEIL-3 1.0 „Fristen“): Neuigkeiten pflegen und die
// Fristen der Tickets. Die Wörter der Zielgruppe stehen in PFLEGE_TEXTE.zielgruppen.
export const NEUIGKEITEN_TEXTE = {
  listenTitel: 'Alle Neuigkeiten', laden: 'Neuigkeiten werden geladen …', leer: 'Noch keine Neuigkeiten.',
  spalten: { titel: 'Titel', kennzeichen: 'Kennzeichen', zielgruppe: 'Zielgruppe', veroeffentlicht: 'Veröffentlicht', aktion: 'Aktion' },
  entwurf: 'Entwurf',
  kennzeichen: { NEU: 'Neu', BEHOBEN: 'Behoben' } as Record<string, string>,
  anlegen: 'Neuigkeit anlegen', dialog: 'Neuigkeit', nichtGefunden: 'Diese Neuigkeit gibt es nicht.',
  feldTitel: 'Titel', feldText: 'Text', zaehler: (anzahl: number) => `${zahl(anzahl)} von 2.000 Zeichen`, feldKennzeichen: 'Kennzeichen',
  zielgruppe: 'Zielgruppe',
  entwurfSpeichern: 'Als Entwurf speichern', veroeffentlichen: 'Veröffentlichen', zurueckziehen: 'Zurückziehen', speichern: 'Änderungen speichern',
  abbrechen: 'Abbrechen',
  frageTitel: 'Neuigkeit veröffentlichen',
  frage: 'Veröffentlichen? Die Neuigkeit erscheint bei allen Kundinnen und Kunden der Zielgruppe als ungelesen.',
  vorschau: 'Vorschau', vorschauSatz: 'So sehen Kundinnen und Kunden die Neuigkeit unter „Hilfe und Feedback“.',
  gespeichert: 'Neuigkeit gespeichert.', veroeffentlicht: 'Neuigkeit veröffentlicht.', zurueckgezogen: 'Neuigkeit zurückgezogen.',
  titelFehler: 'Bitte 3 bis 120 Zeichen.', textFehler: 'Bitte 10 bis 2.000 Zeichen.',
} as const;

// F-24 (SPEC-verwaltung 2.8): Feldhinweise höchstens 12 Wörter.
export const FRISTEN_TEXTE = {
  titel: 'Fristen', satz: 'Gezählt werden Kalendertage in Wien.', laden: 'Fristen werden geladen …',
  felder: {
    erinnerungTage: { label: 'Erinnerung nach … Tagen',
      erklaerung: (produkt: string) => `Wartet die Anfrage so lange auf Antwort, erinnert ${produkt} die Person einmal.` },
    autoGeloestTage: { label: 'Automatisch gelöst nach … Tagen',
      erklaerung: (_produkt: string) => 'Bleibt die Antwort so lange aus, gilt die Anfrage als gelöst.' },
    autoGeschlossenTage: { label: 'Automatisch geschlossen nach … Tagen',
      erklaerung: (_produkt: string) => 'So lange nach „Gelöst“ schließt sie sich, wenn niemand widerspricht.' },
  },
  speichern: 'Fristen speichern', gespeichert: 'Fristen gespeichert.',
  fehler: (min: number, max: number) => `Bitte eine ganze Zahl von ${min} bis ${max} eingeben.`,
} as const;

// PF-331 (F-21; Server PF-328 „Vertretung“, „CSV“, PF-329 „Kennzahlen“, PF-330-b „Anmeldeprobleme“): Abwesenheit und Vertretung,
// CSV-Export der Kennzahlen, Bewertungen und Kostendeckel des Hilfe-Assistenten, Anfragen von der Anmeldeseite.
export const F21_TEXTE = {
  vertretung: {
    titel: 'Abwesenheit und Vertretung',
    erklaerung: 'Während einer Abwesenheit gehen neue Zuweisungen und die Benachrichtigungen an die Vertretung.',
    laden: 'Abwesenheiten werden geladen …', leer: 'Niemand ist abwesend.',
    zeile: (name: string, von: string, bis: string, vertretung: string) => `${name}: ${von} bis ${bis}, vertreten durch ${vertretung}`,
    jetzt: 'jetzt abwesend', beenden: 'Abwesenheit beenden', beendet: 'Abwesenheit beendet.',
    nurEigene: 'Abwesenheiten anderer Konten darf nur die Plattform-Verwaltung eintragen.',
    eintragen: 'Abwesenheit eintragen', fuerWen: 'Für wen?', von: 'Von', bis: 'Bis', vertretung: 'Vertretung', waehlen: 'Bitte wählen',
    speichern: 'Abwesenheit speichern',
    gespeichert: (von: string, bis: string, name: string) => `Gespeichert. Neue Zuweisungen gehen von ${von} bis ${bis} an ${name}.`,
    veraltet: 'Die Daten wurden zwischenzeitlich geändert. Bitte neu laden.',
  },
  // PF-331-nach-2 (Wortliste W1-G11): der Knopf heißt wie jeder Export „Als Tabelle herunterladen (für Excel)“ (geldWoerter.ts).
  csv: { knopf: GELD.tabelleHerunterladen, datei: 'support-kennzahlen.csv', fehler: 'Der Export ist gerade nicht möglich. Bitte versuchen Sie es erneut.' },
  bewertung: {
    titel: 'Anteil hilfreich', ohne: '—',
    wert: (prozent: string, anzahl: number) => `${prozent} % von ${zahl(anzahl)} ${anzahl === 1 ? 'Bewertung' : 'Bewertungen'}`,
  },
  kostendeckel: {
    erreicht: 'Kostendeckel erreicht: Der Hilfe-Assistent ist bis zum Monatsende für alle aus.',
    nichtErreicht: 'Kostendeckel in diesem Monat nicht erreicht.',
    verbraucht: (verbraucht: string, deckel: string, monat: string) => `Verbraucht: ${verbraucht} von ${deckel} (${monat})`,
  },
  zuweisung: {
    abwesend: (bis: string, name: string) => ` (abwesend bis ${bis}, vertreten durch ${name})`,
    vertretung: (an: string, fuer: string) => `Zugewiesen an ${an}, in Vertretung von ${fuer}.`,
  },
  anmeldung: {
    ohneKonto: 'ohne Konto', personOhneKonto: (email: string) => `Ohne Konto · ${email}`, kontaktadresse: 'Kontaktadresse',
    mailHinweis: (email: string) => `Diese Antwort geht per E-Mail an ${email}. Schreiben Sie bitte nichts Vertrauliches hinein.`,
  },
} as const;

// PF-811-c (F-21 Sollte 14): Sperrgrund der Recherche im Ticketkopf, Betreibersicht ohne Beträge.
export const SPERR_TEXTE = {
  zeile: 'Recherche',
  gesperrt: (grund: string) => `Gesperrt: ${grund}`,
  unbekannt: (code: string) => `Gesperrt (Grund ${code})`,
  gruende: { TEAM: 'Teamkontingent erreicht', PERSON: 'Persönliche Grenze erreicht', FIRMA: 'Kontingent der Firma aufgebraucht',
    GUTHABEN: 'Zusätzliches Guthaben aufgebraucht', OBERGRENZE: 'Obergrenze der Firma erreicht' } as Record<string, string>,
  einstellung: 'Beim Anbieter fehlt eine Einstellung für die KI',
} as const;
/** Zeile „Recherche“ im Ticketkopf nur, wenn der Zugang gesperrt ist; sonst null. COST_UNCONFIGURED hat einen eigenen Satz, ein
 *  unbekannter Grund steht mit seinem Code da. */
export function sperrText(zugang: { status: string; grund: string | null } | null | undefined) {
  if (!zugang || zugang.status === 'ACTIVE') return null;
  if (zugang.status === 'COST_UNCONFIGURED') return SPERR_TEXTE.gesperrt(SPERR_TEXTE.einstellung);
  const wort = zugang.grund ? SPERR_TEXTE.gruende[zugang.grund] : undefined;
  return wort ? SPERR_TEXTE.gesperrt(wort) : SPERR_TEXTE.unbekannt(zugang.grund ?? zugang.status);
}

// PF-313-3 (BAU-HILFE.md 9.1.2, 9.1.3, A-11, A-12; TEIL-3 SP3-05, SP3-08): Störungen pflegen und Feature-Wünsche prüfen.
export const STOERUNGEN_TEXTE = {
  listenTitel: 'Alle Störungen', laden: 'Störungen werden geladen …', leer: 'Noch keine Störungen erfasst.',
  // F-23 (PF-NB-hilfe-2, H-05): automatisch erkannte Probleme der Quellen über der Liste (StoerungenPflegen.tsx QuellenHinweis).
  quellen: {
    titel: 'Automatisch erkannt',
    zeile: (quelle: string, zustand: string, ursache: string | null) => `${quelle}: ${zustand}${ursache ? ` – ${ursache}` : ''}`,
    titelVorgabe: (quelle: string) => `${quelle} eingeschränkt`,
    oeffnen: 'Zustand der Quellen öffnen',
    satz: 'Firmen sehen eine Störung erst, wenn Sie sie anlegen und veröffentlichen.',
  },
  spalten: { titel: 'Titel', status: 'Status', auswirkung: 'Auswirkung', bereiche: 'Bereiche', beginn: 'Beginn', sichtbar: 'Sichtbar' },
  veroeffentlicht: 'Veröffentlicht', nichtVeroeffentlicht: 'Nicht veröffentlicht',
  zurueck: 'Zurück zu den Störungen', detailLaden: 'Die Störung wird geladen …', nichtGefunden: 'Diese Störung gibt es nicht.',
  werte: { titel: 'Angaben zur Störung', status: 'Status', auswirkung: 'Auswirkung', bereiche: 'Bereiche', beginn: 'Beginn', behobenAm: 'Behoben am', sichtbar: 'Sichtbar' },
  bearbeiten: 'Bearbeiten', bearbeitenTitel: 'Störung bearbeiten', speichern: 'Änderungen speichern', gespeichert: 'Änderungen gespeichert.',
  meldungen: 'Meldungen', meldungHinzufuegen: 'Meldung hinzufügen', meldung: 'Meldung', status: 'Status', meldungGespeichert: 'Meldung hinzugefügt.',
  veroeffentlichen: 'Veröffentlichen', veroeffentlichenTitel: 'Störung veröffentlichen',
  veroeffentlichenFrage: 'Störung veröffentlichen? Alle angemeldeten Konten sehen sie; ab „Teilweise eingeschränkt“ erscheint sie als Leiste in der App.',
  veroeffentlichtMeldung: 'Störung veröffentlicht.',
  behoben: 'Als behoben melden', anfragenLoesen: 'Verknüpfte Anfragen als gelöst markieren', behobenMeldung: 'Störung als behoben gemeldet.',
  verknuepft: 'Verknüpfte Tickets', keineTickets: 'Keine Tickets verknüpft.',
  ticketZeile: (referenz: string, status: string) => `${referenz} · ${status}`,
  bereicheListe: (woerter: string[]) => woerter.join(', '),
} as const;

export const WUENSCHE_TEXTE = {
  status: { NEU: 'Neu', GEPRUEFT: 'Geprüft', GEPLANT: 'Geplant', UMGESETZT: 'Umgesetzt', ABGELEHNT: 'Abgelehnt' } as Record<string, string>,
  zusammengefuehrtMit: (referenz: string) => `Zusammengeführt mit ${referenz}`,
  zusammengefuehrt: 'Zusammengeführt',
  moderation: { PRUEFUNG: 'In Prüfung', FREIGEGEBEN: 'Freigegeben', ABGELEHNT: 'In der Prüfung abgelehnt' } as Record<string, string>,
  sichtbarkeit: { OEFFENTLICH: 'Alle Kundenfirmen', FIRMA: 'Nur eine Firma' } as Record<string, string>,
  segmente: { zu_pruefen: 'Zu prüfen', alle: 'Alle', geplant: 'Geplant', umgesetzt: 'Umgesetzt', abgelehnt: 'Abgelehnt' } as Record<string, string>,
  segmentGruppe: 'Auswahl der Wünsche',
  suche: 'Referenz oder Titel', suchen: 'Suchen',
  spalten: { referenz: 'Referenz', titel: 'Titel', sichtbarkeit: 'Sichtbarkeit', status: 'Status', stimmen: 'Stimmen', firmen: 'Firmen', eingereicht: 'eingereicht' },
  leer: 'Keine Wünsche in dieser Auswahl.', laden: 'Wünsche werden geladen …',
  zurueck: 'Zurück zu den Wünschen', detailLaden: 'Der Wunsch wird geladen …', nichtGefunden: 'Diesen Wunsch gibt es nicht.',
  titel: (referenz: string, titel: string) => `${referenz} · ${titel}`,
  werte: { titel: 'Angaben zum Wunsch', status: 'Status', pruefung: 'Prüfung', sichtbarkeit: 'Sichtbarkeit', stimmen: 'Stimmen', firmen: 'Firmen',
    eingereicht: 'Eingereicht', herkunft: 'Herkunft' },
  vomBetreiber: 'Eingetragen vom Betreiber',
  herkunft: (firma: string | null, person: string | null) => [person, firma].filter(Boolean).join(' · '),
  titelFeld: 'Titel', beschreibung: 'Beschreibung',
  kuerzen: 'Vor der Freigabe kürzen', kuerzungSpeichern: 'Kürzung speichern', kuerzungGespeichert: 'Kürzung gespeichert.',
  statusFeld: 'Status', statusGespeichert: 'Status gespeichert.',
  ablehnen: 'Ablehnen', grund: 'Grund', grundNeu: 'Den Grund sieht nur die einreichende Person.',
  grundOeffentlich: 'Der Grund steht öffentlich als Antwort beim Wunsch.', abgelehnt: 'Wunsch abgelehnt.',
  antwort: (produkt: string) => `Antwort von ${produkt}`, antwortZaehler: (anzahl: number) => `${zahl(anzahl)} von 2.000 Zeichen`,
  antwortSpeichern: 'Antwort speichern', antwortGespeichert: 'Antwort gespeichert.',
  zusammen: 'Zusammenführen mit …', zusammenTitel: 'Zusammenführen mit',
  zusammenFrage: (quelle: string, ziel: string) => `${quelle} mit ${ziel} zusammenführen? Stimmen und Folgende gehen zu ${ziel}.`,
  zusammenKnopf: 'Zusammenführen', zusammenKeine: 'Kein offener Wunsch gefunden.', zusammenTreffer: 'Offene Wünsche',
  zusammenZeile: (referenz: string, titel: string) => `${referenz} ${titel}`,
  abbrechen: 'Abbrechen',
} as const;

/** Wort zum Status einer Störung (TEIL-3 SP3-05); unbekannt „—“. */
export const stoerungStatusWort = (status: string) => TICKET_TEXTE.stoerungStatus[status] ?? OHNE_WORT;
/** Wort zum Status eines Wunschs (A-11); zusammengeführt mit Ziel „Zusammengeführt mit {WUN-…}“. */
export function wunschStatusWort(status: string, ziel?: string | null) {
  if (status === 'ZUSAMMENGEFUEHRT') return ziel ? WUENSCHE_TEXTE.zusammengefuehrtMit(ziel) : WUENSCHE_TEXTE.zusammengefuehrt;
  return WUENSCHE_TEXTE.status[status] ?? OHNE_WORT;
}
export const moderationWort = (moderation: string) => WUENSCHE_TEXTE.moderation[moderation] ?? OHNE_WORT;
export const wunschSichtbarkeitWort = (sichtbarkeit: string) => WUENSCHE_TEXTE.sichtbarkeit[sichtbarkeit] ?? OHNE_WORT;

const DATUM = new Intl.DateTimeFormat('de-AT', { timeZone: 'Europe/Vienna', day: '2-digit', month: '2-digit', year: 'numeric' });
/** ISO-Zeitpunkt → „14.10.2026“ in Wien. */
export const datum = (iso: string) => DATUM.format(new Date(iso));

const TICKET_REFERENZ = /^SUP-[23456789ABCDEFGHJKLMNPQRSTUVWXYZ]{7}$/;
const WUNSCH_REFERENZ = /^WUN-[23456789ABCDEFGHJKLMNPQRSTUVWXYZ]{5}$/;
const KENNUNG = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export type ArbeitsplatzParameter = { ticket: string | null; stoerung: string | null; wunsch: string | null; dokument: string | null;
  neuigkeit: string | null; filter: ListenFilter; zeitraum: Zeitraum };
const passt = (wert: string | null, muster: RegExp) => (wert !== null && muster.test(wert) ? wert : null);
/** Die sieben eigenen Adressparameter des Arbeitsplatzes, geprüft; unbekannte Werte fallen auf die Vorgabe. ansicht und
 *  support gehören der Verwaltung und kommen hier nicht vor. */
export function arbeitsplatzParameter(search: string): ArbeitsplatzParameter {
  const p = new URLSearchParams(search);
  const filter = p.get('filter'), zeitraum = p.get('zeitraum');
  return {
    ticket: passt(p.get('ticket'), TICKET_REFERENZ),
    stoerung: passt(p.get('stoerung'), KENNUNG),
    wunsch: passt(p.get('wunsch'), WUNSCH_REFERENZ),
    dokument: passt(p.get('dokument'), KENNUNG),
    neuigkeit: passt(p.get('neuigkeit'), KENNUNG),
    filter: (LISTEN_FILTER as readonly string[]).includes(filter ?? '') ? filter as ListenFilter : 'wartet_auf_uns',
    zeitraum: (ZEITRAEUME as readonly string[]).includes(zeitraum ?? '') ? zeitraum as Zeitraum : '30',
  };
}
